import React, { useState, useEffect } from 'react';
import { MessageSquare, Mic, Square, Save, Loader, Edit, Trash2, Bell, CheckCircle, RotateCcw, X, UserCircle, MapPin } from 'lucide-react';
import api from '@/services/api';
import { BuyerNote, ClientReminder, Client } from './types';

interface NotesTabProps {
  // Note props
  notes: BuyerNote[];
  newNote: string;
  setNewNote: (value: string) => void;
  onAddTextNote: (content: string) => Promise<void>;
  isRecording: boolean;
  recordingDuration: number;
  startRecording: () => void;
  stopRecording: () => void;
  isTranscribing: boolean;
  onDeleteNote: (id: number) => void;
  onUpdateNote: (id: number, content: string) => Promise<void>;
  // Reminder props
  reminders: ClientReminder[];
  currentUserRole?: string;
  currentUserId?: number;
  currentUser?: any;
  client?: Client | null;
  onCreateReminder: (reminderData: { content: string; reminder_datetime?: string; visible_to_user_ids?: number[]; timezone_offset?: number; local_time_str?: string }) => Promise<void>;
  onUpdateReminder: (id: number, reminderData: { content?: string; reminder_datetime?: string; status?: string }) => Promise<void>;
  onDeleteReminder: (id: number) => Promise<void>;
  onToggleReminderComplete: (id: number, isCompleted: boolean) => Promise<void>;
  onSnoozeReminder: (id: number, newDatetime: string) => Promise<void>;
}

export const NotesTab: React.FC<NotesTabProps> = ({
  notes,
  newNote,
  setNewNote,
  onAddTextNote,
  isRecording,
  recordingDuration,
  startRecording,
  stopRecording,
  isTranscribing,
  onDeleteNote,
  onUpdateNote,
  currentUserRole,
  currentUserId: _currentUserId,
  currentUser,
  // Reminder props
  reminders,
  onCreateReminder,
  onUpdateReminder,
  onDeleteReminder,
  onToggleReminderComplete,
  onSnoozeReminder,
  client,
}) => {
  // Note editing
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');

  // Active form: null | 'note' | 'reminder'
  const [activeForm, setActiveForm] = useState<null | 'note' | 'reminder'>(null);

  // New reminder form
  const [newReminderContent, setNewReminderContent] = useState('');
  const [newReminderDate, setNewReminderDate] = useState('');
  const [newReminderTime, setNewReminderTime] = useState('08:00');
  const [isLoadingReminder, setIsLoadingReminder] = useState(false);

  // User selection for reminders
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [reminderVisibility, setReminderVisibility] = useState('self');
  const [reminderVisibleTo, setReminderVisibleTo] = useState<string[]>([]);

  // Reminder editing / snooze
  const [editingReminderId, setEditingReminderId] = useState<number | null>(null);
  const [editReminderContent, setEditReminderContent] = useState('');
  const [editReminderDate, setEditReminderDate] = useState('');
  const [editReminderTime, setEditReminderTime] = useState('');
  const [snoozingReminderId, setSnoozingReminderId] = useState<number | null>(null);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [snoozeTime, setSnoozeTime] = useState('08:00');

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secondsRemaining = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secondsRemaining.toString().padStart(2, '0')}`;
  };

  const startEdit = (note: BuyerNote) => {
    setEditingId(note.id);
    setEditContent(note.content || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const saveEdit = async (id: number) => {
    if (!editContent.trim()) return;
    await onUpdateNote(id, editContent);
    setEditingId(null);
    setEditContent('');
  };

  useEffect(() => {
    setLoadingUsers(true);

    if ((currentUserRole === 'super_admin' || currentUserRole === 'admin' || currentUserRole === 'apporteur')) {
      // Fetch all admins and combine with client admin and current user
      let users: any = []
      api.get('/users/administration/all-users')
        .then(res => {
          const allUsers = Array.isArray(res.data) ? res.data : [];
          const adminUsers = allUsers.filter(user => user.role === 'admin');

          // Start with all admins
          if (currentUserRole === 'super_admin') {
            users = adminUsers.map(user => ({
              id: user.id,
              email: user.email,
              first_name: user.first_name || undefined,
              last_name: user.last_name || undefined,
              role: user.role
            }));
          }

          // Add client admin if not already in the list
          if (client && client.admin && !users.find((u: any) => u.id === client?.admin?.id)) {
            users.push({
              id: client.admin.id,
              email: client.admin.email,
              first_name: client.admin.first_name || undefined,
              last_name: client.admin.last_name || undefined,
              role: 'apporteur'
            });
          }

          // Add current user if not already in the list
          if (currentUser && !users.find((u: any) => u.id === currentUser.id)) {
            users.push({
              id: currentUser.id,
              email: currentUser.email,
              first_name: currentUser.first_name || undefined,
              last_name: currentUser.last_name || undefined,
              role: currentUser.role
            });
          }

          setAvailableUsers(users);
        })
        .catch(() => {
          // Fallback to client admin and current user
          users = client?.admin ? [{
            id: client.admin.id,
            email: client.admin.email,
            first_name: client.admin.first_name || undefined,
            last_name: client.admin.last_name || undefined,
            role: 'admin'
          }] : [];

          if (currentUser && client?.admin && currentUser.id !== client.admin.id) {
            users.push({
              id: currentUser.id,
              email: currentUser.email,
              first_name: currentUser.first_name || undefined,
              last_name: currentUser.last_name || undefined,
              role: currentUser.role
            });
          }

          setAvailableUsers(users);
        })
        .finally(() => setLoadingUsers(false));
    } else {
      setAvailableUsers([]);
      setLoadingUsers(false);
    }
  }, [activeForm, currentUserRole, client]);

  // Reminder functions
  const handleCreateReminder = async () => {
    if (!newReminderContent.trim()) return;
    setIsLoadingReminder(true);
    try {
      let reminderDateTime = undefined;
      if (newReminderDate) {
        reminderDateTime = new Date(`${newReminderDate}T${newReminderTime || '08:00'}`).toISOString();
      }
      // Convert selected user IDs to numbers for visible_to_user_ids
      const visibleToUserIds = reminderVisibility === 'specific' && reminderVisibleTo.length > 0
        ? reminderVisibleTo.map(id => Number(id))
        : reminderVisibility === 'self' && currentUser
          ? [Number(currentUser.id)]
          : [];

      // Get user's timezone offset in minutes from UTC (e.g., -60 for UTC+1)
      const timezoneOffset = -new Date().getTimezoneOffset();

      // Format local time string exactly as displayed in UI (e.g., "19/04/2026 à 11:28")
      const localTimeStr = newReminderDate
        ? `${newReminderDate.split('-').reverse().join('/')} à ${newReminderTime || '08:00'}`
        : undefined;

      await onCreateReminder({
        content: newReminderContent,
        reminder_datetime: reminderDateTime,
        visible_to_user_ids: visibleToUserIds,
        timezone_offset: timezoneOffset,
        local_time_str: localTimeStr,
      });
      setNewReminderContent('');
      setNewReminderDate('');
      setNewReminderTime('08:00');
      setReminderVisibility('self');
      setReminderVisibleTo([]);
      setActiveForm(null);
    } catch (error) {
      console.error('Error creating reminder:', error);
    } finally {
      setIsLoadingReminder(false);
    }
  };

  const startEditReminder = (reminder: ClientReminder) => {
    setEditingReminderId(reminder.id);
    setEditReminderContent(reminder.content || '');
    if (reminder.reminder_datetime) {
      const reminderDate = toUtcDate(reminder.reminder_datetime);
      const y = reminderDate.getFullYear();
      const m = String(reminderDate.getMonth() + 1).padStart(2, '0');
      const d = String(reminderDate.getDate()).padStart(2, '0');
      setEditReminderDate(`${y}-${m}-${d}`);
      setEditReminderTime(`${String(reminderDate.getHours()).padStart(2, '0')}:${String(reminderDate.getMinutes()).padStart(2, '0')}`);
    }
  };

  const cancelEditReminder = () => {
    setEditingReminderId(null);
    setEditReminderContent('');
    setEditReminderDate('');
    setEditReminderTime('');
  };

  const saveEditReminder = async (id: number) => {
    if (!editReminderContent.trim()) return;

    setIsLoadingReminder(true);
    try {
      let reminderDateTime = undefined;
      if (editReminderDate) {
        const dateTime = new Date(`${editReminderDate}T${editReminderTime || '08:00'}`);
        reminderDateTime = dateTime.toISOString();
      }

      await onUpdateReminder(id, {
        content: editReminderContent,
        reminder_datetime: reminderDateTime
      });

      setEditingReminderId(null);
      setEditReminderContent('');
      setEditReminderDate('');
      setEditReminderTime('');
    } catch (error) {
      console.error('Error updating reminder:', error);
    } finally {
      setIsLoadingReminder(false);
    }
  };

  const toggleReminderComplete = async (reminder: ClientReminder) => {
    const isCompleted = reminder.status === 'completed';
    await onToggleReminderComplete(reminder.id, !isCompleted);
  };

  const getReminderStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
      case 'pending': return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400';
      case 'snoozed': return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400';
      case 'cancelled': return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      default: return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const toUtcDate = (dt: string) => new Date(dt.endsWith('Z') || dt.includes('+') ? dt : dt + 'Z');

  const formatReminderDateTime = (dateTimeStr: string) => {
    return toUtcDate(dateTimeStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-in fade-in duration-300">
      <div className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border-2 border-slate-200 dark:border-slate-700 p-3 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 sm:mb-6 flex items-center gap-2">
          <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500" />
          Notes & Rappels
        </h2>

        {/* ── Two top action buttons ─────────────────────────── */}
        <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
          <button
            onClick={() => setActiveForm(activeForm === 'note' ? null : 'note')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 font-medium text-sm transition-all ${activeForm === 'note'
              ? 'bg-blue-600 text-white border-blue-600 shadow-md'
              : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30'
              }`}
          >
            <MessageSquare className="w-4 h-4" />
            {activeForm === 'note' ? 'Fermer' : 'Nouvelle note'}
          </button>
          <button
            onClick={() => setActiveForm(activeForm === 'reminder' ? null : 'reminder')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 font-medium text-sm transition-all ${activeForm === 'reminder'
              ? 'bg-orange-600 text-white border-orange-600 shadow-md'
              : 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900/30'
              }`}
          >
            <Bell className="w-4 h-4" />
            {activeForm === 'reminder' ? 'Fermer' : 'Nouveau rappel'}
          </button>
        </div>

        {/* ── Note form ─────────────────────────────────────── */}
        {activeForm === 'note' && (
          <div className="bg-blue-50/50 dark:bg-blue-900/10 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-3 sm:p-4 mb-4 space-y-3">
            <h3 className="font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-2 text-sm">
              <MessageSquare className="w-4 h-4" /> Nouvelle note
            </h3>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Écrire une nouvelle note..."
              rows={3}
              className="w-full px-3 py-2 text-sm border rounded-lg dark:bg-slate-800 dark:border-slate-700 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none resize-none"
            />
            {isRecording && (
              <div className="flex items-center justify-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg animate-pulse">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-base font-mono font-bold text-red-600 dark:text-red-400">🎤 {formatDuration(recordingDuration)}</span>
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              </div>
            )}
            {isTranscribing && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader className="w-4 h-4 animate-spin" /> Transcription en cours...
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <div>
                {!isRecording ? (
                  <button onClick={startRecording} disabled={isTranscribing}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                  >
                    <Mic className="w-4 h-4" /><span className="hidden sm:inline">Vocal</span>
                  </button>
                ) : (
                  <button onClick={stopRecording}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 animate-pulse"
                  >
                    <Square className="w-4 h-4" /><span className="hidden sm:inline">Arrêter</span>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setActiveForm(null)}
                  className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >Annuler</button>
                <button onClick={() => { onAddTextNote(newNote); setActiveForm(null); }}
                  disabled={!newNote.trim() || isTranscribing}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  <Save className="w-4 h-4" /> Enregistrer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Reminder form ─────────────────────────────────── */}
        {activeForm === 'reminder' && (
          <div className="bg-orange-50/50 dark:bg-orange-900/10 border-2 border-orange-200 dark:border-orange-800 rounded-xl p-3 sm:p-4 mb-4 space-y-3">
            <h3 className="font-semibold text-orange-800 dark:text-orange-200 flex items-center gap-2 text-sm">
              <Bell className="w-4 h-4" /> Nouveau rappel
            </h3>
            <textarea
              value={newReminderContent}
              onChange={(e) => setNewReminderContent(e.target.value)}
              placeholder="Commentaire du rappel..."
              rows={2}
              className="w-full px-3 py-2 text-sm border rounded-lg dark:bg-slate-800 dark:border-slate-600 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none resize-none"
            />
            <div className="flex flex-wrap gap-2">
              <input type="date" value={newReminderDate}
                onChange={(e) => setNewReminderDate(e.target.value)}
                className="flex-1 min-w-[130px] px-3 py-2 text-sm border rounded-lg dark:bg-slate-800 dark:border-slate-600 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
              />
              <input type="time" value={newReminderTime}
                onChange={(e) => setNewReminderTime(e.target.value)}
                className="w-28 px-3 py-2 text-sm border rounded-lg dark:bg-slate-800 dark:border-slate-600 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
              />
            </div>

            {/* Visibility — admin/super_admin only (hidden for apporteur since only 'self' option) */}
            {(currentUserRole === 'admin' || currentUserRole === 'super_admin') && (
              <div className="space-y-2 pt-1">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Visible dans « Mes Rappels » pour :
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {([
                    ['self', 'Moi uniquement'],
                    ['specific', 'Utilisateurs spécifiques']
                  ] as [string, string][]).map(([val, label]) => (
                    <label key={val} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="vis" value={val}
                        checked={reminderVisibility === val}
                        onChange={() => setReminderVisibility(val)}
                        className="accent-orange-500"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
                    </label>
                  ))}
                </div>
                {reminderVisibility === 'specific' && (
                  <div className="mt-1">
                    {loadingUsers ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500"><Loader className="w-4 h-4 animate-spin" /> Chargement...</div>
                    ) : availableUsers.length === 0 ? (
                      <p className="text-sm text-slate-400">Aucun utilisateur disponible</p>
                    ) : (
                      <div className="max-h-40 overflow-y-auto border rounded-lg dark:border-slate-600 divide-y divide-slate-100 dark:divide-slate-700 bg-white dark:bg-slate-800">
                        {availableUsers.map(u => {
                          const uid = String(u.id);
                          const checked = reminderVisibleTo.includes(uid);
                          return (
                            <label key={u.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-orange-50 dark:hover:bg-orange-900/10">
                              <input type="checkbox" checked={checked}
                                onChange={() => setReminderVisibleTo(prev =>
                                  checked ? prev.filter(x => x !== uid) : [...prev, uid]
                                )}
                                className="accent-orange-500"
                              />
                              <span className="text-sm text-slate-700 dark:text-slate-300 flex-1">
                                {currentUser && u.id === currentUser.id ? 'Moi' :
                                  ((u.first_name || u.last_name) ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : u.email)}
                              </span>
                              {u.role && <span className="text-[10px] text-slate-400 uppercase">{u.role}</span>}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <button
                onClick={() => { setActiveForm(null); setNewReminderContent(''); setNewReminderDate(''); setNewReminderTime('08:00'); setReminderVisibility('self'); setReminderVisibleTo([]); }}
                className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >Annuler</button>
              <button onClick={handleCreateReminder}
                disabled={!newReminderContent.trim() || isLoadingReminder}
                className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
              >
                {isLoadingReminder ? <Loader className="w-4 h-4 animate-spin" /> : <><Bell className="w-3.5 h-3.5" /> Créer le rappel</>}
              </button>
            </div>
          </div>
        )}

        {/* ── Old recording indicator outside note form (fallback) ─ */}
        {isRecording && activeForm !== 'note' && (
          <div className="flex items-center justify-center gap-2 p-3 mb-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg animate-pulse">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-base font-mono font-bold text-red-600 dark:text-red-400">🎤 {formatDuration(recordingDuration)}</span>
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          </div>
        )}

        <div className="space-y-6">
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-md shrink-0">
                <MessageSquare className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
              </span>
              Notes
            </h3>
            {notes.length === 0 ? (
              <div className="text-center py-6 text-slate-500 dark:text-slate-400">
                <p className="text-sm sm:text-base">Aucune note pour le moment</p>
              </div>
            ) : (
              notes.map((note) => (
                <div key={note.id} className={`rounded-lg sm:rounded-xl p-3 sm:p-5 border border-slate-100 dark:border-slate-700/50 border-l-4 ${note.note_type === 'voice'
                  ? 'bg-purple-50/40 dark:bg-slate-800/50 border-l-purple-400 dark:border-l-purple-500'
                  : 'bg-blue-50/30 dark:bg-slate-800/50 border-l-blue-400 dark:border-l-blue-500'
                  }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${note.note_type === 'voice'
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30'
                        }`}>
                        {note.note_type === 'voice' ? '🎤 Vocal' : '📝 Texte'}
                      </span>
                      <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">
                        {new Date(note.created_at).toLocaleString('fr-FR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEdit(note)}
                        className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDeleteNote(note.id)}
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {editingId === note.id ? (
                    <div className="space-y-3">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg dark:bg-slate-900 dark:border-slate-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                        rows={3}
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={cancelEdit}
                          className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={() => saveEdit(note.id)}
                          disabled={!editContent.trim()}
                          className="px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                          Enregistrer
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {note.audio_url && (
                        <div className="mb-4">
                          <audio controls className="w-full h-8 opacity-80 hover:opacity-100 transition-opacity">
                            <source src={note.audio_url} type="audio/webm" />
                          </audio>
                        </div>
                      )}

                      {note.transcription && (
                        <div className="mb-3 p-3 bg-amber-50/50 dark:bg-amber-900/10 border-l-4 border-amber-400 rounded-r-lg">
                          <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-1">Transcription automatique</p>
                          <p className="text-sm text-slate-700 dark:text-slate-300 italic leading-relaxed">"{note.transcription}"</p>
                        </div>
                      )}

                      {note.content && (
                        <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                          {note.content}
                        </p>
                      )}
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 bg-orange-100 dark:bg-orange-900/30 rounded-md shrink-0">
                <Bell className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" />
              </span>
              Rappels
            </h3>
            {reminders.length === 0 ? (
              <div className="text-center py-6 text-slate-500 dark:text-slate-400">
                <p className="text-sm sm:text-base">Aucun rappel pour le moment</p>
              </div>
            ) : (
              reminders.map((reminder) => (
                <div key={reminder.id} className="bg-orange-50/30 dark:bg-slate-800/50 rounded-lg sm:rounded-xl p-3 sm:p-5 border border-orange-100 dark:border-slate-700/50 border-l-4 border-l-orange-400 dark:border-l-orange-500">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30">
                        ⏰ Rappel
                      </span>
                      {reminder.status && (
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${getReminderStatusColor(reminder.status)}`}>
                          {reminder.status === 'completed' ? '✓ Terminé' : reminder.status === 'pending' ? '⏰ En attente' : reminder.status === 'snoozed' ? '🔄 Reporté' : '❌ Annulé'}
                        </span>
                      )}
                      {reminder.reminder_datetime && (
                        <span className="text-[10px] font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wider">
                          📅 {formatReminderDateTime(reminder.reminder_datetime)}
                        </span>
                      )}
                      {reminder.created_by_name && (
                        <span className="flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500">
                          <UserCircle className="w-3 h-3" />{reminder.created_by_name}
                        </span>
                      )}
                      {client?.city && (
                        <span className="flex items-center gap-0.5 text-[10px] font-medium text-teal-600 dark:text-teal-400">
                          <MapPin className="w-3 h-3" />{client.city}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleReminderComplete(reminder)}
                        className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                        title={reminder.status === 'completed' ? 'Marquer comme non terminé' : 'Marquer comme terminé'}
                      >
                        <CheckCircle className={`w-4 h-4 ${reminder.status === 'completed' ? 'text-green-600' : ''}`} />
                      </button>
                      {reminder.status !== 'completed' && (
                        <button
                          onClick={() => { setSnoozingReminderId(reminder.id); setSnoozeDate(''); setSnoozeTime('08:00'); }}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Reporter à une autre date"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => startEditReminder(reminder)}
                        className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDeleteReminder(reminder.id)}
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {snoozingReminderId === reminder.id && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg space-y-2">
                      <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 flex items-center gap-1">
                        <RotateCcw className="w-3.5 h-3.5" /> Reporter à :
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="date"
                          value={snoozeDate}
                          onChange={e => setSnoozeDate(e.target.value)}
                          className="flex-1 min-w-0 px-2 py-1.5 text-sm border rounded-lg dark:bg-slate-800 dark:border-slate-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <input
                          type="time"
                          value={snoozeTime}
                          onChange={e => setSnoozeTime(e.target.value)}
                          className="w-24 px-2 py-1.5 text-sm border rounded-lg dark:bg-slate-800 dark:border-slate-600 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <button
                          onClick={async () => {
                            if (!snoozeDate) return;
                            const iso = new Date(`${snoozeDate}T${snoozeTime || '08:00'}:00`).toISOString();
                            await onSnoozeReminder(reminder.id, iso);
                            setSnoozingReminderId(null);
                          }}
                          disabled={!snoozeDate || isLoadingReminder}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Confirmer
                        </button>
                        <button
                          onClick={() => setSnoozingReminderId(null)}
                          className="p-1.5 text-slate-500 hover:text-slate-800 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {editingReminderId === reminder.id ? (
                    <div className="space-y-3">
                      <textarea
                        value={editReminderContent}
                        onChange={(e) => setEditReminderContent(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg dark:bg-slate-900 dark:border-slate-600 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none resize-none"
                        rows={3}
                        placeholder="Commentaire du rappel..."
                      />
                      <div className="flex gap-2">
                        <input
                          type="date"
                          value={editReminderDate}
                          onChange={(e) => setEditReminderDate(e.target.value)}
                          className="flex-1 px-3 py-2 border rounded-lg dark:bg-slate-900 dark:border-slate-600 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
                        />
                        <input
                          type="time"
                          value={editReminderTime}
                          onChange={(e) => setEditReminderTime(e.target.value)}
                          className="px-3 py-2 border rounded-lg dark:bg-slate-900 dark:border-slate-600 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={cancelEditReminder}
                          className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={() => saveEditReminder(reminder.id)}
                          disabled={!editReminderContent.trim() || isLoadingReminder}
                          className="px-3 py-1.5 text-sm bg-orange-600 text-white hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {isLoadingReminder ? <Loader className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {reminder.content && (
                        <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                          {reminder.content}
                        </p>
                      )}

                      {reminder.status === 'completed' && (
                        <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/10 border-l-4 border-green-400 rounded-r-lg">
                          <p className="text-sm text-green-700 dark:text-green-300">
                            ✅ Rappel terminé{reminder.completed_at ? ` le ${new Date(reminder.completed_at).toLocaleDateString('fr-FR')}` : ''}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
